import { Box3, OrthographicCamera, Vector2, Vector3, type Object3D, type Scene, type WebGLRenderer } from "three";

/**
 * As quatro vistas do visor (F120), pela anatomia: os bonecos olham para −Z, então a direita deles é
 * +X (frente × cima; os GLBs conferem: `handslotr` em +x, os dedos do pé à frente do pé em −z). A
 * câmera "direita" fica do lado direito do personagem; nada é espelhado.
 */
export const TURNAROUND_VIEWS = [
  { name: "frente", dir: new Vector3(0, 0, -1) },
  { name: "direita", dir: new Vector3(1, 0, 0) },
  { name: "esquerda", dir: new Vector3(-1, 0, 0) },
  { name: "costas", dir: new Vector3(0, 0, 1) },
] as const;

export type TurnaroundView = (typeof TURNAROUND_VIEWS)[number]["name"];

/** Folga do enquadramento em volta da caixa. */
const MARGIN = 1.2;

/** O mínimo do three para desenhar as quatro (o renderer, ou um falso nos testes). */
export type TurnaroundRenderer = Pick<WebGLRenderer, "getSize" | "setScissorTest" | "setViewport" | "setScissor" | "render">;

/**
 * Câmeras ortográficas das quatro vistas com o MESMO pivô (o centro da caixa dos bonecos), a mesma
 * distância e a mesma escala: a mesma pose, a mesma luz e o mesmo modelo, de quatro lados. A escala
 * cobre a altura e a maior largura (x ou z: a da frente ou a do perfil) no aspecto do quadrante.
 */
export class Turnaround {
  readonly cameras: OrthographicCamera[] = TURNAROUND_VIEWS.map(() => new OrthographicCamera());
  readonly center = new Vector3();
  /** A caixa enquadrada (m). */
  readonly extent = new Vector3(1, 1, 1);
  /** Meia altura do enquadramento no último desenho (m). */
  half = 1;
  private readonly size = new Vector2();
  private readonly box = new Box3();

  /**
   * Enquadra `targets` pela caixa exata (com o skin) da pose atual. `keepScale`: só move o pivô (o
   * roteiro de andar: o boneco anda e a escala não pula com a pose).
   */
  frame(targets: readonly Object3D[], keepScale = false): void {
    this.box.makeEmpty();
    for (const t of targets) {
      t.updateMatrixWorld(true);
      this.box.expandByObject(t, true);
    }
    this.box.getCenter(this.center);
    if (!keepScale) this.box.getSize(this.extent);
    const radius = this.extent.length() / 2;
    this.cameras.forEach((camera, i) => {
      camera.position.copy(this.center).addScaledVector(TURNAROUND_VIEWS[i]!.dir, radius * 3);
      camera.up.set(0, 1, 0);
      camera.lookAt(this.center);
      camera.near = 0.01;
      camera.far = radius * 6;
    });
  }

  /** A meia altura que cabe a caixa num quadrante de aspecto `aspect` (largura ÷ altura). */
  halfFor(aspect: number): number {
    return MARGIN * Math.max(this.extent.y / 2, Math.max(this.extent.x, this.extent.z) / (2 * aspect));
  }

  /** Desenha as quatro no canvas, 2 × 2: frente e direita em cima, esquerda e costas embaixo. */
  render(renderer: TurnaroundRenderer, scene: Scene): void {
    renderer.getSize(this.size);
    const w = this.size.x / 2;
    const h = this.size.y / 2;
    const aspect = w / h;
    this.half = this.halfFor(aspect);
    renderer.setScissorTest(true);
    this.cameras.forEach((camera, i) => {
      camera.left = -this.half * aspect;
      camera.right = this.half * aspect;
      camera.top = this.half;
      camera.bottom = -this.half;
      camera.updateProjectionMatrix();
      // Viewport do three: origem embaixo à esquerda; a primeira linha (0 e 1) fica em cima.
      const x = (i % 2) * w;
      const y = i < 2 ? h : 0;
      renderer.setViewport(x, y, w, h);
      renderer.setScissor(x, y, w, h);
      renderer.render(scene, camera);
    });
    renderer.setScissorTest(false);
    renderer.setViewport(0, 0, this.size.x, this.size.y);
  }
}
